import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Button,
  Form,
  Icon,
  Image,
  Input,
  InputProps,
  Label,
  Modal,
  Dropdown,
} from "semantic-ui-react";

import ProxyPatientSerializer from "@/issara-sdk/types/ProxyPatientSerializer_apps_PRX";
import Cookies from "js-cookie";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";

import DiagFormSerializer from "issara-sdk/types/DiagFormSerializer_apps_PRX";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";
import PatientNoteSerializer from "issara-sdk/types/PatientNoteSerializer_apps_REG";
// Serializers
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";

// Interface
import { CartData, DiagFormData, DiagFormFilter, PatientAppointmentListCard } from "./StaffChatInterface";

// Common
import ModInfo from "react-lib/apps/common/ModInfo";

import SnackMessage from "../common/SnackMessage";

import CardCreateChatRoom from "./CardCreateChatRoom";
import DisplayGroupName from "./DisplayGroupName";
// Local components
import ModConfirmCustom from "./ModConfirmCustom";
import ProfilePanel from "./ProfilePanel";
import SearchBox from "./SearchBox";
import StaffChatBox from "./StaffChatBox";

import CONFIG from "config/config";
import ChatbotSerializer from "@/issara-sdk/types/ChatbotSerializer_core";
import MessageContentHTML from "../IsHealth/Chat/MessageContentHTML";

const CardDiagFormHistory = lazy(async () => import("../IsHealth/Nurse/CardDiagFormHistory"));

type StaffChatProps = {
  onEvent: (e: any) => any;
  setProp: any;
  errorMessage: any;
  chatController: any;
  diagFormController?: any;
  className?: string;
  apiToken: string;
  chatRoomList?: any[];
  divisionId: string;
  isChatPatientOnly?: boolean;
  modOpenLinkEncounter?: string;
  searchMessageList?: {
    count: number;
    current_limit: number;
    current_offset: number;
    items: any[];
    next: any;
    previous: any;
  };
  searchUserList?: any[];
  userFirstName: string;
  userId: string;
  userLastName: string;
  userName: string;
  menuSameLevelTextArea?: boolean;
  selectedPatientChat?: PatientSerializer;
  selectedPatientAppointmentList?: PatientAppointmentSerializer[];
  officialAccountList?: any;
  selectedPatientDiagFormList?: DiagFormSerializer[];
  selectedPatientNoteList?: PatientNoteSerializer[];
  selectedProxyPatient?: ProxyPatientSerializer;
  diagFormData: DiagFormData;
  onCloseModLinkEncounter?: () => any;
  onDidMount?: () => any;
  openPatientChatId?: number | null;
  onOpenVideoCall?: (url: string, chat_channel_id: string) => any;
  onReadAllMessages?: () => void;
  aiBotLists?: { divisionId: number; aiLists: ChatbotSerializer[] };
  // --- สำหรับ BookingSection (SelectDatetime flow) ---
  createAppointment?: any;
  timeDivision?: any[];
  loadingStatus?: Record<string, any>;
  createdAppointment?: any;
  // --- สำหรับ CartSection (ProfilePanel tab "cart") ---
  cartData?: CartData;
};

type LatestMessageProps = { data: Record<string, any>; userId: string };

type TabType = "profile" | "form" | "booking" | "note" | "cart";

const COOKIE_KEY = "active_tabs";

const COLORS = {
  chat_light: "var(--primary-theme-chat-light)",
  dark_grey: "#746A6A",
  divider: "rgba(228, 218, 218, 0.5)",
  grey: "rgb(204, 204, 204)",
};

const IMAGES = {
  leave: "/static/images/leave-icon.png",
  pencil_cog: "/static/images/pencil-cog-icon.png",
  person: "/static/images/person.png",
};

const styles = {
  item: {
    alignItems: "center",
    borderBottom: `1px solid ${COLORS.divider}`,
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    padding: "15px 10px",
  } satisfies React.CSSProperties,
  no_chat: {
    alignItems: "center",
    color: "rgb(158 157 157)",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "center",
  } satisfies React.CSSProperties,
};

const formatDatetime = (datetime: string) => {
  if (!datetime) {
    return "";
  }

  const date = moment(datetime);
  const today = moment();

  // เช็คว่าเป็นวันเดียวกันหรือไม่
  const isToday = date.isSame(today, "day");

  return isToday ? date.format("HH:mm") : date.format("DD/MM/YYYY");
};

const LatestMessage = (props: LatestMessageProps) => {
  const intl = useIntl();

  const messageTypeMap: Record<string, string> = {
    file: "ส่งไฟล์",
    image: "ส่งรูปภาพ",
    video: "ส่งวิดีโอ",
  };

  const messageTypeId = messageTypeMap[props.data.latest_message_content_type || ""];

  if (messageTypeId) {
    return (
      <>
        {props.data.latest_message_author === Number(props.userId) ? (
          <>{intl.locale === "th" ? "คุณ" : "You "}</>
        ) : (
          <>{props.data.latest_message_author_name} </>
        )}
        <FormattedMessage id={messageTypeId} />
      </>
    );
  }

  return  <MessageContentHTML content={props.data.latest_message_content}/>;
};

const StaffChat = React.forwardRef<any, StaffChatProps>((props, ref) => {
  // mod create class room
  const [openModal, setOpenModal] = useState<{
    id?: number;
    chat_channel_id?: number;
    chat_channel_name_autogen?: string;
    members?: any[];
    name?: string;
    open?: true;
  } | null>(null);
  const [openModLeave, setOpenModLeave] = useState<boolean>(false);

  const [toggleSearch, setToggleSearch] = useState<boolean>(false);
  const [selectedChat, setSelectedChat] = useState<any>(null);
  const [inputSearch, setInputSearch] = useState<string>("");
  const [chatRoomList, setChatRoomList] = useState<any[]>([]);
  const [searchIndex, setSearchIndex] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [openModDiagHis, setOpenModDiagHis] = useState<boolean>(false);
  const [showProfilePanel, setShowProfilePanel] = useState<boolean>(true);
  const [tabsByChat, setTabsByChat] = useState<Map<string, TabType[]>>(new Map());
  const [isUnread, setIsUnread] = useState<boolean>(false);

  const chatBoxRef = useRef<any>(null);
  const isFinish = useRef<boolean>(false);
  const isSearch = useRef<boolean>(false);
  const channelIdRef = useRef<number | null>(null);
  const openPatientIdRef = useRef<number | null>(null);
  const [selectedAIBot, setSelectedAIBot] = useState<string>("");
  const intl = useIntl();

  const userId = useMemo(() => props.userId, [props.userId]);

  useImperativeHandle(ref, () => ({
    chatBoxRef: chatBoxRef.current,

    getChatChannelList: (params: any) => {
      isFinish.current = true;
      handleGetChatChannelList(params);
    },
  }));

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const channelId = params.get("channel_id");

    if (channelId) {
      channelIdRef.current = Number(channelId);

      const url = new URL(globalThis.location.origin);

      url.searchParams.set("app", "HIS");
      globalThis.history.replaceState({}, "", url);
    }

    props.onDidMount?.();
  }, []);

  useEffect(() => {
    if (!channelIdRef.current || (props.chatRoomList ?? []).length <= 0) {
      return;
    }

    const foundChat = props.chatRoomList?.find(
      (item) => item.chat_channel_id === channelIdRef.current,
    );

    if (foundChat) {
      handleSelectedChat(foundChat)();
    }

    channelIdRef.current = null;
  }, [props.chatRoomList]);

  // เปิดห้องแชทของผู้รับบริการที่ส่งมาจากหน้าอื่น เมื่อได้รายการแชทล่าสุด
  useEffect(() => {
    if (!openPatientIdRef.current || !props.chatRoomList) {
      return;
    }

    const foundChat = props.chatRoomList.find(
      (item: any) => item.patient === openPatientIdRef.current,
    );

    if (foundChat) {
      handleSelectedChat(foundChat)();
    } else {
      props.setProp(
        "errorMessage.StaffChat",
        intl.formatMessage({ id: "ไม่พบห้องแชทของผู้รับบริการรายนี้" }),
      );
    }

    openPatientIdRef.current = null;
  }, [props.chatRoomList]);

  useEffect(() => {
    if (!props.openPatientChatId) {
      return;
    }

    openPatientIdRef.current = props.openPatientChatId;
    props.setProp("openPatientChatId", null);

    handleGetChatChannelList();
  }, [props.openPatientChatId]);

  useEffect(() => {
    setSelectedChat(null);

    handleGetChatChannelList();
    handleGetDiagFormList();
    handleGetFormCategoryList();
    handleGetUsableAIList();
  }, [props.divisionId]);

  useEffect(() => {
    let list: any[] = [...(props.chatRoomList || [])];
    const index = list.findIndex((item) => item.id === selectedChat?.id);

    if (index !== -1) {
      list[index].unread_message_count = 0;
    }

    list = list.filter((item) => {
      if (props.isChatPatientOnly) {
        return item.division;
      } else {
        return item.division == null;
      }
    });

    setChatRoomList(list);
  }, [props.chatRoomList]);

  useEffect(() => {
    const checkReadMessageCount = () => {
      const sum = chatRoomList.reduce((result, item) => (result += item.unread_message_count), 0);

      if (!sum && !!chatRoomList.length) {
        props.onReadAllMessages?.();
      }
    };

    checkReadMessageCount();
  }, [chatRoomList]);

  useEffect(() => {
    if (!chatRoomList?.find((item) => item.id === selectedChat?.id)) {
      setSelectedChat(null);
      handleClose();
    } else if (isFinish.current && !!selectedChat) {
      const list: any[] = props.chatRoomList || [];
      const chat = list.find((item) => item.id === selectedChat?.id);
      const modal = list.find((item) => item.id === openModal?.id);

      isFinish.current = false;

      if (!!modal) {
        handleOpenModal(modal);
      }
      setSelectedChat(chat || null);
    }
  }, [props.chatRoomList]);

  const searchMessage = useMemo(() => {
    const item = props.searchMessageList?.items?.[searchIndex];
    return item && { id: item.id, offset: item.offset, index: searchIndex };
  }, [searchIndex, props.searchMessageList?.items]);

  useEffect(() => {
    const raw = Cookies.get(COOKIE_KEY);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      const userData = parsed[userId] || {};
      const map = new Map<string, TabType[]>(Object.entries(userData));

      setTabsByChat(map);
    } catch (err) {
      console.error("Invalid Cookie, ", err);
    }
  }, [userId]);

  const aiSelectOptions = useMemo(() => {
    return [
      { key: "human", value: "human", text: "normal chat" },
      ...(props.aiBotLists?.aiLists.map((ai: any) => ({
        key: ai.name,
        value: ai.name,
        text: ai.name,
      })) || []),
    ];
  }, [props.aiBotLists]);

  /* ------------------------- API ------------------------ */
  const handleGetChatChannelList = (params = {}) => {
    props.onEvent({
      message: "GetChatChannelList",
      params: {
        channelId: "all",
        user: props.userId,
        ...(isSearch.current && { search: inputSearch }),
        ...params,
        chatRoomId: selectedChat?.id,
        isChatPatientOnly: props.isChatPatientOnly,
      },
    });
  };

  const handleGetSearchUserList = useCallback((search: string, selected: any[]) => {
    props.onEvent({
      message: "GetSearchUserList",
      params: { search, is_set_state: true, selected: selected },
    });
  }, []);

  const handleGetUserChatSubscription = useCallback((params: any) => {
    props.onEvent({
      message: "GetUserChatSubscriptionList",
      params,
    });
  }, []);

  const handleCreateUpdateChatRoom = (data: any) => {
    return props.onEvent({
      message: "CreateUpdateChatRoom",
      params: data,
    });
  };

  const handleGetSearchMessageList = (params: any) => {
    props.onEvent({
      message: "GetSearchMessageList",
      params: params,
    });
  };

  const handleGetDiagFormList = () => {
    props.onEvent({ message: "GetDiagFormList", params: { division: props.divisionId } });
  };

  const handleGetFormCategoryList = () => {
    props.onEvent({ message: "GetFormCategoryList", params: { division: props.divisionId } });
  };

  const handleGetUsableAIList = () => {
    props.onEvent({ message: "GetUsableAIList", params: { divisionId: props.divisionId } });
  };

  /* ------------------------- API ------------------------ */

  // Delete
  const handleDeleteMember = useCallback(
    (params: { ids: number[]; name?: string; isChangeName?: boolean }) => {
      return handleCreateUpdateChatRoom({
        chatChannelId: selectedChat?.chat_channel_id,
        type: "delete",
        userIds: params.ids,
        groupName: params.name,
        isChangeName: params.isChangeName,
      });
    },
    [selectedChat],
  );

  const handleFinish = useCallback(
    async (data: any) => {
      const userIds = [...data.memberIds, Number(props.userId)].sort((a: any, b: any) => a - b);
      const findChat = chatRoomList.find((item) => {
        const memberIds: any[] = item.members
          .map((acc: any) => acc.user_obj.id)
          .sort((a: any, b: any) => a - b);
        return userIds.toString() === memberIds.toString();
      });

      let result: any[] = [null, null, null];

      if (!!findChat && !data.chatChannelId) {
        setSelectedChat(findChat);
      } else {
        result = await handleCreateUpdateChatRoom({
          ...data,
          type: "create",
          notChangeName: data.groupName === openModal?.name && data.chatChannelId,
        });

        if (!!selectedChat) {
          chatBoxRef.current?.receivedMessage?.({
            chatChannelId: selectedChat?.chat_channel_id,
          });
          isFinish.current = true;
        }

        await handleGetChatChannelList({
          search: inputSearch,
          channelId: openModal?.chat_channel_id || result[0]?.channel,
        });
      }

      handleClose();
    },
    [props.userId, inputSearch, openModal, selectedChat, chatRoomList],
  );

  // Open Modal
  const handleOpenModal = (params: any = {}) => {
    setOpenModal({ open: true, ...params });
  };

  const handleClose = () => {
    setOpenModal(null);
  };

  // Toggle Search
  const handleToggleSearch = () => {
    setToggleSearch(!toggleSearch);
    handleGetSearchMessageList({ clear: true });
    handleClearSearch();
  };

  const handleCloseSearch = () => {
    setToggleSearch(false);
    handleClearSearch();
    handleGetSearchMessageList({ clear: true });
  };

  const handleClearSearch = () => {
    setSearchText("");
    setSearchIndex(0);
  };

  const handleSearch = (value: string) => {
    handleGetSearchMessageList({
      search: value,
      chatChannelId: selectedChat?.chat_channel_id,
      clear: !value,
      last: true,
    });

    setSearchText(value);
    setSearchIndex(0);
  };

  const handleNextSearch = (value: string) => {
    handleGetSearchMessageList({
      search: value,
      chatChannelId: selectedChat?.chat_channel_id,
      previous: props.searchMessageList?.previous,
      concat: true,
    });
  };

  // Chat
  const handleSelectedChat = (item: any) => () => {
    // ล้างรายการนัดหมายของคนเก่าทันที (กันประวัตินัดคนเก่าค้างใน panel)
    props.setProp?.("selectedPatientAppointmentList", []);

    // ตั้ง loading เฉพาะแชทที่มี patient จริง (ProfilePanel จะเรียก GetPatientAppointmentList)
    // ไม่งั้น loading จะค้างเพราะไม่มีใครไปโหลดข้อมูล
    if (item?.patient) {
      props.setProp?.(`loadingStatus.${PatientAppointmentListCard}`, true);
    }

    setSelectedChat(null);
    handleCloseSearch();
    handleClearSearch();

    if (item.unread_message_count) {
      const message = {
        event: "message_read",
        data_message_type: "MESSAGE_READ",
        message_id: item.latest_message,
        channel_id: item.chat_channel_id,
      };

      // ส่งข้อความกลับไปยังแท็บเดิม
      window.opener?.postMessage(message, window.location.origin);
    }

    setTimeout(() => setSelectedChat(item));
  };

  // Leave
  const handleOpenModLeave = () => {
    setOpenModLeave(true);
  };

  const handleCloseModLeave = () => {
    setOpenModLeave(false);
  };

  const handleConfirmLeave = async () => {
    await handleCreateUpdateChatRoom({
      userChatSubscriptionId: selectedChat?.user_chat_subscription_id,
      groupName: "",
      chatChannelId: selectedChat?.chat_channel_id,
      type: "leave",
      isChangeName: !!selectedChat.name && selectedChat?.member_count - 1 === 1,
    });

    handleCloseModLeave();
    setSelectedChat(null);

    await handleGetChatChannelList();
  };

  // Search
  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      handleGetChatChannelList({ search: (e.target as any).value });
      isSearch.current = true;
      isFinish.current = true;
    }
  };

  const handleChangeInput = (e: any, v: InputProps) => {
    isSearch.current = false;
    setInputSearch(v.value);
  };

  // Badge
  const handleUnreadMessage = (action: "add" | "reset", chatId: number) => {
    const update: any[] = chatRoomList.map((item) =>
      item.chat_channel_id === chatId
        ? {
            ...item,
            unread_message_count: action === "add" ? item.unread_message_count + 1 : 0,
          }
        : item,
    );
    setChatRoomList(update);
  };

  const handleReadMessage = () => {
    handleUnreadMessage("reset", selectedChat?.chat_channel_id);
  };

  // Message list
  const handleSelectMessage = (item: any, index: number) => {
    setSearchIndex(index);
  };

  const handlePostMessage = () => {
    if (searchText) {
      handleCloseSearch();
    }
  };

  const handleClickLink = (e: MouseEvent) => {
    const a = e.target as HTMLAnchorElement;
    const href = a.href || "";
    const params = new URLSearchParams(href.match(/(?!=\/)\?.*$/g)?.[0] || "");
    const encounterId = params.get("encounterId");
    const app = params.get("app");

    if (encounterId && app === "MainDPO") {
      if (e.type === "mousedown") {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      props.onEvent({
        message: "HandleOpenLinkEncounter",
        params: { type: e.type, event: e, encounterId, href },
      });
    } else {
      const clickLeft = e?.button === 0;

      if (clickLeft && href) {
        window.open(href, "_blank", "noopener noreferrer")?.focus();
      }
    }
  };

  const handleOpenModDiagHis = () => {
    setOpenModDiagHis(true);
  };

  const handleCloseModDiagHis = () => {
    setOpenModDiagHis(false);
  };

  const handleToggleProfilePanel = () => {
    setShowProfilePanel(!showProfilePanel);
  };

  const handleSaveNote = (note: string) => {
    if (!selectedChat?.patient) return;

    const patient = selectedChat.patient;

    props.onEvent({ message: "SavePatientNote", params: { patient, note } });
  };

  const handleUpdateNote = (id: number, note: string) => {
    if (!id) return;
    if (!selectedChat?.patient) return;

    const patient = selectedChat?.patient;

    props.onEvent({ message: "UpdatePatientNote", params: { id, patient, note } });
  };

  const handleDeleteNote = (id: number) => {
    if (!id || !selectedChat?.patient) return;

    const patient = selectedChat.patient;

    props.onEvent({ message: "DeletePatientNote", params: { id, patient } });
  };

  const handleSearchDiagForm = (filter: DiagFormFilter, activePage: number) => {
    if (!selectedChat?.patient && !selectedChat?.division) return;

    const patient = selectedChat.patient;
    const division = selectedChat.division;

    props.onEvent({
      message: "GetPatientDiagFormFilter",
      params: {
        patient,
        division,
        activePage,
        filter,
      },
    });
  };

  // --- ProfilePanel (ใช้เก็บ activeTabs ของ Chat นั้น ๆ)
  const toggleProfilePanelTab = (tab: TabType) => {
    const chatKey = (selectedChat?.patient ?? "default").toString();

    setTabsByChat((prev) => {
      const newMap = new Map(prev);
      const currentTabs = newMap.get(chatKey) || [];
      const updatedTabs = currentTabs.includes(tab)
        ? currentTabs.filter((t) => t !== tab)
        : [...currentTabs, tab];

      newMap.set(chatKey, updatedTabs);

      let cookieData: any = {};
      const raw = Cookies.get(COOKIE_KEY);
      if (raw) {
        try {
          cookieData = JSON.parse(raw);
        } catch {}
      }

      cookieData[userId] = Object.fromEntries(newMap);

      Cookies.set(COOKIE_KEY, JSON.stringify(cookieData));

      return newMap;
    });
  };

  const handleChangeChatChannelList = () => {
    const unreadOnly = !isUnread;
    setIsUnread(unreadOnly);

    handleGetChatChannelList({ unread_only: unreadOnly });
  };

  const activeProfilePanelTabs = tabsByChat.get(String(selectedChat?.patient)) || [];

  // ─── Cart tab: ส่งลิงก์ชำระเงิน (mehealthCheckout=true) เข้าแชทของลูกค้า ───
  // GetCartPaymentLink (StaffChatInterface) สร้าง link + ข้อความ → chatController post เข้า channel
  // chatController ชุดเดียวกันกับที่ ChatBox ใช้ส่งข้อความของ staff เอง
  const handleSendCartPaymentLink = async () => {
    const chatChannelId = selectedChat?.chat_channel_id;

    if (!chatChannelId) {
      props.setProp?.(`errorMessage.StaffChat`, "ไม่พบช่องทางแชทของผู้รับบริการ");

      return null;
    }

    const result = await props.onEvent({
      message: "GetCartPaymentLink",
      params: { patient: selectedChat?.patient },
    });

    // handler เขียน errorMessage[GET_CART_PAYMENT_LINK] ไว้ให้ CartSection แสดงในบรรทัดนั้นแล้ว
    if (!result?.message) return null;

    // StaffChat บางจุด receives chatController แบบ spread msgManager มาทับ
    // → เช็คทั้งสอง path และกัน case ไม่มี method (destructure จาก undefined จะพังทั้งฟังก์ชัน)
    const postChatChannelMessage =
      props.chatController?.postChatChannelMessage ??
      props.chatController?.msgManager?.postChatChannelMessage;

    if (typeof postChatChannelMessage !== "function") {
      props.setProp?.(`errorMessage.StaffChat`, "ระบบแชทไม่พร้อมส่งข้อความในขณะนี้");

      return null;
    }

    const [, error] = await postChatChannelMessage({
      apiToken: props.apiToken,
      content: result.message,
      contentType: "text",
      chatChannelId,
      divisionId: Number(props.divisionId),
    });

    if (error) {
      props.setProp?.(`errorMessage.StaffChat`, "ส่งลิงก์เข้าแชทไม่สำเร็จ กรุณาลองอีกครั้ง");

      return null;
    }

    // ข้อความที่ยิงจากนอก ChatBox ไม่มี optimistic ในลิสต์ → ดึง message list ใหม่ให้ขึ้นทันที
    chatBoxRef.current?.receivedMessage?.({ chatChannelId });

    return { link: result.link, cartCount: result.cartCount };
  };

  return (
    <div className={`staff-chat ${props.className}`} style={{ height: "95vh" }}>
      {/* --- ErrorMessage --- */}
      <SnackMessage
        error={props.errorMessage?.StaffChat}
        success={null}
        onClose={() => props.setProp(`errorMessage.StaffChat`, null)}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "22.5% 1fr auto",
          height: "100%",
        }}
      >
        {/* ChatList */}
        <div style={{ borderRight: `1px solid ${COLORS.grey}` }}>
          {/* Filter */}
          <Form style={{ padding: "10px 0px 5px 10px" }}>
            <Form.Group className="search-chat-room" inline>
              <Form.Field width={16}>
                <Input
                  value={inputSearch}
                  action={{
                    icon: "search",
                    onClick: () =>
                      handleKeyPress({
                        key: "Enter",
                        target: { value: inputSearch },
                      }),
                  }}
                  placeholder="Search..."
                  onKeyPress={handleKeyPress}
                  onChange={handleChangeInput}
                />
              </Form.Field>

              {!props.isChatPatientOnly && (
                <Form.Field style={{ minWidth: "max-content" }} width={6}>
                  <Button
                    style={{
                      backgroundColor: COLORS.chat_light,
                      padding: ".78rem 0.5rem .78rem",
                    }}
                    fluid
                    onClick={() => handleOpenModal()}
                  >
                    <Icon name="facebook messenger" />
                    New Chat
                  </Button>
                </Form.Field>
              )}
            </Form.Group>
          </Form>

          <Form style={{ padding: "0 10px" }}>
            <Form.Group>
              {["ทั้งหมด", "ยังไม่ได้อ่าน"].map((label, index) => {
                const active = index === 1 ? isUnread : !isUnread;
                return (
                  <Form.Field width={8} key={label}>
                    <Button
                      fluid
                      size="small"
                      style={
                        active ? { background: "var(--primary-theme-color)", color: "#fff" } : {}
                      }
                      onClick={handleChangeChatChannelList}
                    >
                      {label}
                    </Button>
                  </Form.Field>
                );
              })}
            </Form.Group>
          </Form>

          {/* Chat */}
          <div className="chat-list" style={{ height: "88vh", overflow: "auto" }}>
            {chatRoomList.length === 0 && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                <div style={{ background: "#f6f7f7", padding: "13px" }}>ไม่พบข้อมูล</div>
              </div>
            )}
            {chatRoomList.map((item, index) => (
              <div
                key={`chat-list-${index}`}
                className="chat-list-item"
                style={{
                  ...styles.item,
                  backgroundColor:
                    selectedChat?.id === item.id && selectedChat?.id
                      ? "var(--primary-select-background)"
                      : "",
                }}
                onClick={handleSelectedChat(item)}
              >
                <div style={{ width: "85%", display: "flex", alignItems: "center" }}>
                  {props.isChatPatientOnly && (
                    <Image
                      style={{
                        borderRadius: "50%",
                        height: "35px",
                        marginRight: "0.55rem",
                        width: "35px",
                      }}
                      alt="chat-image.icon"
                      src={item.image || IMAGES.person}
                    />
                  )}
                  <div style={{ overflow: "hidden", width: "100%" }}>
                    <DisplayGroupName
                      style={{ display: "flex", justifyContent: "space-between" }}
                      memberCount={item.member_count}
                      name={props.isChatPatientOnly ? item.chat_channel_name_autogen : item.name}
                      nameAutoGen={item.chat_channel_name_autogen}
                      rightLabel={
                        props.isChatPatientOnly ? formatDatetime(item.latest_message_datetime) : ""
                      }
                    />
                    {props.isChatPatientOnly && (
                      <div
                        style={{
                          color: "rgba(117, 117, 117, 1)",
                          display: "block",
                          fontSize: "10px",
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          ...(item.latest_message_content && { marginBottom: "-2px" }),
                        }}
                      >
                        <LatestMessage data={item} userId={props.userId} />
                      </div>
                    )}
                  </div>
                </div>

                {!!item.unread_message_count && (
                  <Label
                    style={{
                      borderRadius: "500rem",
                      color: "white",
                      fontSize: "0.6rem",
                      padding: 0,
                      ...(!props.isChatPatientOnly && {
                        fontSize: 0,
                        height: "8px",
                        width: "8px",
                      }),
                    }}
                    color="red"
                    circular
                  >
                    {item.unread_message_count > 99 ? "99+" : item.unread_message_count}
                  </Label>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ChatBox */}
        <div>
          {selectedChat ? (
            <>
              <div style={{ borderBottom: `1px solid ${COLORS.grey}` }}>
                <div
                  className="search-bar"
                  style={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "15px 10px 15px",
                  }}
                >
                  <div style={{ alignItems: "center", display: "flex" }}>
                    {props.isChatPatientOnly && (
                      <Image
                        style={{
                          borderRadius: "50%",
                          height: "35px",
                          marginRight: "0.6rem",
                          width: "35px",
                        }}
                        alt="chat-image.icon"
                        src={selectedChat.image || IMAGES.person}
                      />
                    )}
                    <DisplayGroupName
                      memberCount={selectedChat.member_count}
                      nameAutoGen={selectedChat.chat_channel_name_autogen}
                      name={
                        props.isChatPatientOnly
                          ? selectedChat.chat_channel_name_autogen
                          : selectedChat.name
                      }
                      // splitter
                    />

                    {!props.isChatPatientOnly && (
                      <Image
                        src={IMAGES.pencil_cog}
                        style={{
                          width: "20px",
                          marginLeft: "4rem",
                          cursor: "pointer",
                        }}
                        alt="pencil-cog.icon"
                        onClick={() => handleOpenModal(selectedChat)}
                      />
                    )}
                  </div>
                  <div style={{ alignItems: "flex-end", display: "flex" }}>
                    <div style={{ alignItems: "center", display: "flex" }}>
                      {/* {props.isChatPatientOnly && (
                        <Button
                          style={{
                            backgroundColor: "white",
                            border: "1px solid var(--primary-theme-color)",
                            color: "var(--primary-theme-color)",
                            marginRight: "0.75rem",
                          }}
                          size="mini"
                          onClick={handleOpenModDiagHis}
                        >
                          {intl.formatMessage({ id: "แบบคัดกรอง" })}
                        </Button>
                      )} */}

                      <Dropdown
                        value={selectedAIBot}
                        onChange={async (e, { value }) => {
                          // then Send WS
                          if (selectedChat?.chat_channel_id) {
                            let res = await props.chatController.postChatChannelMessage({
                              content: value,
                              contentType: "ai_selection",
                              chatChannelId: selectedChat?.chat_channel_id,
                              apiToken: props.apiToken,
                              divisionId: props.divisionId,
                            });
                            console.log("res: ", res);

                            if (res) {
                              setSelectedAIBot(value as string);
                            }
                          }
                        }}
                        options={aiSelectOptions}
                        fluid
                        selection
                      />
                      <Icon
                        style={{
                          color: COLORS.dark_grey,
                          cursor: "pointer",
                          fontSize: "1.25rem",
                          margin: "0px 0.5rem",
                        }}
                        name="search"
                        onClick={handleToggleSearch}
                      />
                      {props.isChatPatientOnly && (
                        <Button
                          icon="info"
                          circular
                          size="tiny"
                          style={{
                            backgroundColor: "var(--primary-theme-color)",
                            fontSize: "0.55rem",
                            color: "white",
                            marginLeft: "1rem",
                          }}
                          onClick={handleToggleProfilePanel}
                        />
                      )}
                      {!props.isChatPatientOnly && (
                        <Image
                          style={{
                            cursor: "pointer",
                            height: "18px",
                            margin: "0px 1rem",
                            width: "18px",
                          }}
                          alt="leave.icon"
                          src={IMAGES.leave}
                          onClick={handleOpenModLeave}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {toggleSearch && (
                  <SearchBox
                    total={props.searchMessageList?.count || 0}
                    activeIndex={searchIndex}
                    searchList={props.searchMessageList?.items}
                    laxyLoad={chatBoxRef.current?.laxyLoad}
                    // callback
                    onClose={handleCloseSearch}
                    onSearch={handleSearch}
                    onNextSearch={handleNextSearch}
                    onChangeIndex={setSearchIndex}
                    onSelectMessage={handleSelectMessage}
                  />
                )}
              </div>

              <div>
                <StaffChatBox
                  ref={chatBoxRef}
                  onEvent={props.onEvent}
                  chatController={props.chatController}
                  apiToken={props.apiToken}
                  divisionId={props.divisionId}
                  hideCallButton={CONFIG.HIDE_CALL_BUTTON}
                  isChatPatientOnly={props.isChatPatientOnly}
                  menuSameLevelTextArea={props.menuSameLevelTextArea}
                  searchMessage={searchMessage}
                  searchText={searchText}
                  selectedChat={selectedChat}
                  toggleSearch={toggleSearch}
                  userId={props.userId}
                  onClickLink={handleClickLink}
                  onDidMount={handleReadMessage}
                  onOpenVideoCall={props.onOpenVideoCall}
                  onPostMessage={handlePostMessage}
                  onSetChannelName={() => {
                    // console.log("Got ChannelName and detail also")
                    let channelDetail = chatBoxRef?.current?.getChannelDetail();
                    console.log("channelDetail: ", channelDetail);

                    // Initial Phase
                    if (selectedAIBot === "") {
                      if (channelDetail?.current_chatbot) {
                        setSelectedAIBot(channelDetail?.current_chatbot);
                      } else if (
                        !channelDetail?.current_chatbot &&
                        channelDetail?.prefer_chatbot &&
                        channelDetail?.is_ai_enable &&
                        channelDetail?.default_chatbot?.name
                      ) {
                        setSelectedAIBot(channelDetail?.default_chatbot?.name);
                      }
                    }

                    // if (channelDetail?.is_ai_enable) {
                    //   // include ai
                    //   if (channelDetail?.current_chatbot) {
                    //     // check valid in

                    //     //TODO:
                    //   }

                    // } else {
                    //   setSelectedAIBot("human")
                    // }
                    // // AI or human
                    // if (channelDetail?.is_ai_enable && channelDetail?.current_chatbot && aiSelectOptions.length > 0) {
                    //   setSelectedAIBot(channelDetail.current_chatbot)
                    //   return
                    // }
                    // // Not set yet use default (no current_chatbot)
                    // if (!channelDetail?.current_chatbot) {

                    //   // AI
                    //   if (channelDetail?.prefer_chatbot) {

                    //   }

                    // }

                    //   && channelDetail?.prefer_chatbot) {
                    //   // AI
                    // } else if ( )

                    console.log("channelDetail: ", channelDetail);
                  }}
                />
              </div>
            </>
          ) : (
            <div style={styles.no_chat}>
              <Icon
                name="comment alternate outline"
                style={{
                  transform: "scale(5.5)",
                  marginBottom: "100px",
                  color: "#e0e0e0",
                }}
              />
              <p style={{ fontSize: "1.25rem", marginTop: "-2rem" }}>No Conversation</p>
            </div>
          )}
        </div>

        {/* Profile Panel */}
        {showProfilePanel && props.isChatPatientOnly && selectedChat && (
          <ProfilePanel
            onEvent={props.onEvent}
            activeTabs={activeProfilePanelTabs}
            selectedChat={selectedChat}
            selectedProxyPatient={props.selectedProxyPatient}
            patientData={props.selectedPatientChat}
            forms={props.selectedPatientDiagFormList}
            appointments={props.selectedPatientAppointmentList}
            officialAccountList={props.officialAccountList}
            notes={props.selectedPatientNoteList}
            diagFormData={props.diagFormData}
            onSearchForm={handleSearchDiagForm}
            onSaveNote={handleSaveNote}
            onUpdateNote={handleUpdateNote}
            onDeleteNote={handleDeleteNote}
            onToggleTab={toggleProfilePanelTab}
            onSendCartPaymentLink={handleSendCartPaymentLink}
            apiToken={props.apiToken}
            createAppointment={props.createAppointment}
            timeDivision={props.timeDivision}
            loadingStatus={props.loadingStatus}
            createdAppointment={props.createdAppointment}
            errorMessage={props.errorMessage}
            setProp={props.setProp}
            cartData={props.cartData}
          />
        )}
      </div>

      {/* Modal */}
      <Modal open={!!openModal} size="small" onClose={handleClose} style={{ width: "50%" }}>
        <CardCreateChatRoom
          // data
          userName={props.userName}
          userLastName={props.userLastName}
          userFirstName={props.userFirstName}
          userId={props.userId}
          groupName={openModal?.name || ""}
          nameAutoGen={openModal?.chat_channel_name_autogen}
          userList={props.searchUserList}
          chatChannelId={openModal?.chat_channel_id}
          memberList={openModal?.members}
          // callback
          onGetSearchUserList={handleGetSearchUserList}
          onGetUserChatSubscription={handleGetUserChatSubscription}
          onEvent={props.onEvent}
          onClose={handleClose}
          onDelete={handleDeleteMember}
          onFinish={handleFinish}
        />
      </Modal>

      <ModConfirmCustom
        open={openModLeave}
        onDeny={handleCloseModLeave}
        onApprove={handleConfirmLeave}
        approveText="Leave"
        denyText="Cancel"
        content={
          <div style={{ margin: "0 0 -1.25rem" }}>
            <label>
              If you leave this group, you will no longer be able to view it's chat history
            </label>
            <div style={{ textAlign: "center", marginTop: "2rem" }}>
              Do you want to leave this group?
            </div>
          </div>
        }
      />

      <ModInfo
        open={!!props.modOpenLinkEncounter}
        titleName="แจ้งเตือน!!!"
        titleColor="red"
        alertText={
          <div
            style={{ margin: "0 0 1.2rem", whiteSpace: "pre-wrap" }}
            dangerouslySetInnerHTML={{ __html: props.modOpenLinkEncounter || "" }}
          />
        }
        onClose={props.onCloseModLinkEncounter}
        onApprove={props.onCloseModLinkEncounter}
      />

      <Modal open={openModDiagHis} size="large" closeOnDimmerClick onClose={handleCloseModDiagHis}>
        <Suspense fallback={<div>Loading...</div>}>
          <CardDiagFormHistory
            controller={props.diagFormController}
            apiToken={props.apiToken}
            hideCallback={handleCloseModDiagHis}
            // @ts-ignore
            patientData={{ patient: selectedChat?.patient }}
            userId={props.userId}
          />
        </Suspense>
      </Modal>
    </div>
  );
});

StaffChat.displayName = "StaffChat";

export default React.memo(StaffChat);
